import React, { useState } from 'react'
import { Package, Download, Plus, CheckCircle, Loader2, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import { createProduct, downloadProductTemplate } from '@/lib/api/products'
import { normalizeToVertical, getVerticalProfile } from '@/lib/verticalUI'
import toast from 'react-hot-toast'

interface Step4ProductsOptionalProps {
  productsCount: number
  businessType?: string | null
  onProductCreated?: () => void
  onSkip: () => void
  onNext: () => void
  onBack: () => void
}

function getItemPlaceholder(vertical: string): string {
  switch (vertical) {
    case 'salud':
      return 'Ej. Consulta General / Pediatría'
    case 'inmobiliaria':
      return 'Ej. Apartamento 2 Hab Las Mercedes'
    case 'belleza_estetica':
      return 'Ej. Corte y Balayage / Manicura Rusa'
    case 'turismo_posadas':
      return 'Ej. Suite Matrimonial Vista al Mar'
    case 'servicios':
      return 'Ej. Asesoría Legal / Mantenimiento Aire'
    case 'educacion':
      return 'Ej. Curso Intensivo de Inglés A1'
    case 'tienda_repuestos':
      return 'Ej. Pastillas de Freno Delanteras Corolla'
    case 'restaurante':
      return 'Ej. Hamburguesa Doble con Papas'
    case 'bodegon_licoreria':
      return 'Ej. Combo Ron Santa Teresa + Refresco'
    case 'ferreteria':
      return 'Ej. Saco de Cemento Gris 42.5kg'
    case 'tecnologia':
      return 'Ej. iPhone 15 Pro Max 256GB'
    default:
      return 'Ej. Producto o servicio principal'
  }
}

export default function Step4ProductsOptional({
  productsCount,
  businessType,
  onProductCreated,
  onSkip,
  onNext,
  onBack,
}: Step4ProductsOptionalProps) {
  const [selectedOption, setSelectedOption] = useState<'skip' | 'manual' | null>(null)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [stock, setStock] = useState('10')
  const [description, setDescription] = useState('')

  const normalizedVertical = normalizeToVertical(businessType)
  const profile = getVerticalProfile(normalizedVertical)
  const isAppointment = !profile.operational.hasFulfillment

  const handleDownloadTemplate = async () => {
    try {
      await downloadProductTemplate()
      toast.success('Plantilla descargada con éxito')
    } catch {
      toast.error('No se pudo descargar la plantilla en este momento')
    }
  }

  const handleQuickAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error(`Ingresa el nombre del ${profile.terminology.itemSingularName.toLowerCase()}`)
      return
    }
    const numPrice = parseFloat(price)
    if (isNaN(numPrice) || numPrice <= 0) {
      toast.error('Ingresa un precio válido mayor a 0')
      return
    }

    try {
      setSaving(true)
      const priceCents = Math.round(numPrice * 100)
      const sku = `PROD-${Date.now().toString(36).toUpperCase()}`
      const stockValue = isAppointment ? 1 : (parseInt(stock, 10) || 1)
      await createProduct({
        name: name.trim(),
        sku,
        priceCents,
        currency: 'USD',
        stock: stockValue,
        description: description.trim() || undefined,
      })

      toast.success(`${profile.terminology.itemSingularName} "${name.trim()}" agregado al catálogo`)
      setName('')
      setPrice('')
      setDescription('')
      onProductCreated?.()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || `Error al guardar ${profile.terminology.itemSingularName.toLowerCase()}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 mb-4 shadow-lg">
          {isAppointment ? (
            <Calendar className="h-8 w-8 text-white" />
          ) : (
            <Package className="h-8 w-8 text-white" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Agrega tus {profile.terminology.itemsPluralName}
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto text-sm">
          {isAppointment
            ? `Puedes registrar tus primeros servicios o especialidades ahora o hacerlo después desde el menú ${profile.terminology.catalogName}. Configurar al menos uno permite que tu bot responda consultas y coordine citas en WhatsApp.`
            : `Puedes agregar tus primeros productos ahora o hacerlo después desde el menú de Catálogo. Tener al menos un producto permite que el bot pueda cotizar y cerrar ventas en WhatsApp.`}
        </p>
      </div>

      {/* Opciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Opción A: Omitir */}
        <button
          type="button"
          onClick={() => setSelectedOption('skip')}
          className={cn(
            "p-6 rounded-2xl border-2 transition-all text-left hover:shadow-lg",
            selectedOption === 'skip'
              ? "border-[#075E54] bg-[#075E54]/5 shadow-lg"
              : "border-gray-200 hover:border-gray-300"
          )}
        >
          <div className="flex items-start gap-4">
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
              selectedOption === 'skip'
                ? "bg-[#075E54] text-white"
                : "bg-gray-100 text-gray-400"
            )}>
              {selectedOption === 'skip' ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <span className="text-2xl">⏭️</span>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Agregar después
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {isAppointment
                  ? `Omite este paso y configura tus ${profile.terminology.itemsPluralName.toLowerCase()} cuando estés listo desde el menú ${profile.terminology.catalogName}.`
                  : `Omite este paso y configura tu inventario cuando estés listo desde el menú Catálogo o Productos.`}
              </p>
              <div className="inline-flex items-center gap-1 text-xs font-medium text-[#075E54] bg-[#075E54]/10 px-3 py-1.5 rounded-full">
                <span>✓</span>
                Recomendado para empezar rápido
              </div>
            </div>
          </div>
        </button>

        {/* Opción B: Agregar ahora */}
        <button
          type="button"
          onClick={() => setSelectedOption('manual')}
          className={cn(
            "p-6 rounded-2xl border-2 transition-all text-left hover:shadow-lg",
            selectedOption === 'manual'
              ? "border-[#075E54] bg-[#075E54]/5 shadow-lg"
              : "border-gray-200 hover:border-gray-300"
          )}
        >
          <div className="flex items-start gap-4">
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
              selectedOption === 'manual'
                ? "bg-[#075E54] text-white"
                : "bg-gray-100 text-gray-400"
            )}>
              {selectedOption === 'manual' ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <Plus className="w-6 h-6" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {isAppointment
                  ? `Registrar ${profile.terminology.itemSingularName.toLowerCase()} rápido`
                  : `Agregar ${profile.terminology.itemSingularName.toLowerCase()} rápido`}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {isAppointment
                  ? `Registra 1 o más ${profile.terminology.itemsPluralName.toLowerCase()} clave ahora para que tu agente pueda orientar y agendar citas de inmediato.`
                  : `Registra 1 o más ${profile.terminology.itemsPluralName.toLowerCase()} clave ahora para que tu agente pueda probar cotizaciones de inmediato.`}
              </p>
              <div className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                <span>{isAppointment ? '📅' : '📦'}</span>
                <span>Crea tu primer {profile.terminology.itemSingularName.toLowerCase()}</span>
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Formulario de carga rápida (solo si selecciona "manual") */}
      {selectedOption === 'manual' && (
        <div className="max-w-4xl mx-auto space-y-4 animate-slideInRight">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#075E54]" />
              Crear {profile.terminology.itemSingularName} Rápido
            </h4>

            <form onSubmit={handleQuickAdd} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nombre del {profile.terminology.itemSingularName.toLowerCase()} *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={getItemPlaceholder(normalizedVertical)}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#075E54]/30"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {isAppointment ? 'Precio / Tarifa (USD) *' : 'Precio (USD) *'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="Ej. 50.00"
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#075E54]/30"
                    disabled={saving}
                  />
                </div>
              </div>

              <div className={cn("grid gap-4", isAppointment ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2")}>
                {!isAppointment && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Stock disponible</label>
                    <input
                      type="number"
                      value={stock}
                      onChange={(e) => setStock(e.target.value)}
                      placeholder="10"
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#075E54]/30"
                      disabled={saving}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Descripción corta (opcional)</label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={
                      isAppointment
                        ? 'Ej. Duración 45 min, incluye revisión y diagnóstico inicial'
                        : 'Ej. Color Titanio Natural, nuevo en caja sellada'
                    }
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#075E54]/30"
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving || !name.trim() || !price}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#075E54] hover:bg-[#064E46] text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>{profile.terminology.createItemCta}</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="inline-flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900 underline"
                >
                  <Download className="w-3.5 h-3.5" />
                  {isAppointment
                    ? `Descargar plantilla Excel para carga masiva de ${profile.terminology.itemsPluralName.toLowerCase()}`
                    : 'Descargar plantilla Excel para carga masiva posterior'}
                </button>
              </div>
            </form>
          </div>

          {productsCount > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">
                <span className="font-semibold">{productsCount} {profile.terminology.itemsPluralName.toLowerCase()}</span> registrado(s) en tu catálogo. Puedes continuar o añadir otro.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Info adicional */}
      <div className="max-w-4xl mx-auto bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-600 text-center">
          {isAppointment ? (
            <>
              <span className="font-medium">¿Deseas personalizar agendas o especialistas?</span> Podrás configurar horarios y disponibilidad desde el menú {profile.terminology.catalogName}.
            </>
          ) : (
            <>
              <span className="font-medium">¿No tienes productos físicos?</span> Puedes omitir este paso si ofreces servicios, asesorías o consultas generales.
            </>
          )}
        </p>
      </div>

      {/* Botones de navegación */}
      <div className="flex justify-between pt-6 border-t max-w-4xl mx-auto">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          ← Atrás
        </button>
        <button
          type="button"
          onClick={selectedOption === 'skip' ? onSkip : onNext}
          disabled={!selectedOption}
          className={cn(
            "px-6 py-2.5 rounded-lg font-medium transition-all",
            selectedOption
              ? "bg-[#075E54] hover:bg-[#064E46] text-white shadow-lg hover:shadow-xl"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          )}
        >
          {selectedOption === 'skip'
            ? 'Omitir y Continuar'
            : (productsCount > 0 ? `Continuar con ${profile.terminology.itemsPluralName.toLowerCase()}` : 'Continuar') + ' →'}
        </button>
      </div>
    </div>
  )
}
